import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  DropdownItem,
  Header,
  Label,
  Loader,
  Table,
} from 'semantic-ui-react';
import { ChargingPolicy } from 'subdomains/cpo/screens/GroupsAndPolicies/types';
import Flex from 'components/Layout/helpers/Flex';
import { useHistory } from 'react-router-dom';
import ConfirmDelete from 'components/ConfirmDelete';
import { request } from 'utils/api';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import Pagination from 'components/Pagination';
import ErrorMessage from 'components/ErrorMessage';

export default function ChargingPolicies() {
  const { t } = useTranslation();
  const historyStack = useHistory();
  const [page, setPage] = useState(1);

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery<{
    data: ChargingPolicy[];
    meta: { total: number; limit: number };
  }>(['/1/charging-policies', page], async () => {
    const { data, meta } = await request<{
      data: ChargingPolicy[];
      meta: { total: number; limit: number };
    }>({
      path: `/1/charging-policies`,
      method: 'GET',
      params: {
        limit: 10,
        offset: (page - 1) * 10,
      },
    });

    return { data, meta };
  });

  const chargingPolicies = response?.data ?? [];
  const total = response?.meta?.total ?? 0;
  const limit = response?.meta?.limit ?? 0;

  return (
    <>
      <Flex direction="row" justify="space-between" align="center">
        <Header as="h2" style={{ marginTop: '1em' }}>
          {t('chargingPoliciesTable.title', 'Charging Policies')}
        </Header>
        <Button
          onClick={() =>
            historyStack.push(
              '/charging-stations/policies-and-groups/charging-policies/new'
            )
          }>
          New Charging Policy
        </Button>
      </Flex>
      <ErrorMessage error={error} />
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Loader active />
        </div>
      )}
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {t(
                'chargingPoliciesTable.columnNameAndDesc',
                'Name & Description'
              )}
            </Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {t('chargingPoliciesTable.columnStatus', 'Status')}
            </Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {t('chargingPoliciesTable.columnCurrency', 'Currency')}
            </Table.HeaderCell>
            <Table.HeaderCell width={4}>
              {t('chargingPoliciesTable.columnPricing', 'Pricing')}
            </Table.HeaderCell>
            <Table.HeaderCell width={3}>
              {t('chargingPoliciesTable.columnAssociated', 'Associated')}
            </Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {t('common.actions', 'Actions')}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {!isLoading && isEmpty(chargingPolicies) && (
            <Table.Row>
              <Table.Cell colSpan={3} style={{ textAlign: 'center' }}>
                {t(
                  'chargingPoliciesTable.noChargingPolicies',
                  'No Charging Policies'
                )}
              </Table.Cell>
            </Table.Row>
          )}
          {chargingPolicies.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <strong>{item.label}</strong>
                <br />
                {item.description}
              </Table.Cell>
              <Table.Cell>
                <ChargingPolicyStatusLabel status={item.status} />
              </Table.Cell>
              <Table.Cell>{item.currency}</Table.Cell>
              <Table.Cell>{item.pricing}</Table.Cell>
              <Table.Cell>
                {item.associatedGroups?.length
                  ? item.associatedGroups.join(', ')
                  : t(
                      'chargingPoliciesTable.noAssociated',
                      'No associated groups'
                    )}
              </Table.Cell>
              <Table.Cell>
                <Fragment>
                  <Button
                    basic
                    icon="pen-to-square"
                    title={t('common.edit', 'Edit')}
                    onClick={() =>
                      historyStack.push(
                        `/charging-stations/policies-and-groups/charging-policies/${item.id}/edit`
                      )
                    }
                  />

                  <Dropdown
                    text=""
                    icon=""
                    trigger={<Button icon="ellipsis-vertical" basic />}>
                    <Dropdown.Menu>
                      <DropdownItem>
                        <Dropdown.Item
                          content={t('common.duplicate', 'Duplicate')}
                          icon="copy"
                        />
                      </DropdownItem>
                      <DropdownItem>
                        <ConfirmDelete
                          onConfirm={async () => {
                            await request({
                              path: `/1/charging-policies/${item.id}`,
                              method: 'DELETE',
                            });
                            refetch();
                          }}
                          name={item.label}
                          objectName={t(
                            'common.chargingPolicy',
                            'Charging Policy'
                          )}
                          trigger={
                            <Dropdown.Item
                              content={t('common.archive', 'Archive')}
                              icon="trash"
                            />
                          }
                        />
                      </DropdownItem>
                    </Dropdown.Menu>
                  </Dropdown>
                </Fragment>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 10,
        }}>
        <Pagination
          className=""
          estimated=""
          limit={limit}
          page={page}
          total={total}
          onPageChange={(
            e: React.MouseEvent<HTMLAnchorElement>,
            { activePage }: { activePage: number }
          ) => {
            setPage(activePage);
            refetch();
          }}
        />
      </div>
    </>
  );
}

function ChargingPolicyStatusLabel({ status }: { status?: string }) {
  const { t } = useTranslation();

  const color = useMemo(() => {
    if (status === 'Active') {
      return 'olive';
    }
    if (status === 'Not in use') {
      return 'grey';
    }
    return 'red';
  }, [status]);

  const content = useMemo(() => {
    if (status === 'Active') {
      return t('chargingPolicyStatus.Active', 'Active');
    }
    if (status === 'Not in use') {
      return t('chargingPolicyStatus.NotInUse', 'Not in use');
    }
    return t('chargingPolicyStatus.Unknown', 'Unknown');
  }, [status]);

  return <Label color={color} content={content} />;
}
